import { useCallback, useEffect, useMemo, useState } from 'react';
import { SponsoredPlacementCard } from '../commercial/SponsoredPlacementCard';
import { clearSponsoredOfferReference, sponsoredOfferReference } from '../commercialSponsoredApi';
import { Icon } from '../ui/Icons';
import { ProgressBar } from '../ui/ProgressBar';
import { UserContextPanel, type UserContextActions, type UserContextData } from '../userContext/UserContext';
import {
  type CollectionEnvelope,
  type DataEnvelope,
  type GuestActivation,
  type GuestOffer,
  type GuestOfferPage,
  GuestOffersApiError,
  type OfferCategory,
  type OfferContext,
  guestOffersFetch,
  offerMissingText,
  offerRequirementText,
  offersPageEndpoint,
  offerStateLabel,
} from '../guestOffersApi';
import '../offers.css';
import '../offers-experience-v1.css';

type OffersScreenProps = OfferContext & {
  cityName?: string;
  onOpenActivation: (activationId: string, offerId: string) => void;
  userContext: UserContextData;
  userContextActions: UserContextActions;
};

type Flash = {
  message: string;
  kind: 'success' | 'error';
};

const CATEGORY_OPTIONS: Array<{ value: OfferCategory; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'available', label: 'Доступные' },
  { value: 'soon', label: 'Скоро' },
  { value: 'locked', label: 'Недоступные' },
];

function activationStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Активировано';
    case 'redeemed':
      return 'Получено';
    case 'expired':
      return 'Истекло';
    case 'revoked':
      return 'Недоступно';
    default:
      return status;
  }
}

function formatOfferDate(value: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function VenueAvatar({ url, name }: { url: string | null; name: string | null }) {
  if (url) {
    return <img className="offer-venue-avatar" src={url} alt="" />;
  }

  return (
    <span className="offer-venue-avatar offer-venue-avatar--placeholder" aria-label={name ?? 'Заведение Хоппера'}>
      <Icon name="location" />
    </span>
  );
}

function OfferCopy({ offer }: { offer: GuestOffer }) {
  const activationStatus = offer.my_activation?.status ?? null;
  const requirementText = offerRequirementText(offer);
  const missingText = offerMissingText(offer);
  const endDate = formatOfferDate(offer.ends_at);
  const showProgress = offer.requirement.percent !== null && offer.state !== 'available';

  return (
    <>
      <div className="offer-card__topline">
        <VenueAvatar url={offer.venue.avatar_url} name={offer.venue.display_name} />
        <div className="offer-card__venue">
          <strong>{offer.venue.display_name ?? 'Заведение Хоппера'}</strong>
          <span>{offer.venue.address ?? 'Адрес уточняется'}</span>
        </div>
        <span className={`offer-status-pill is-${activationStatus ?? offer.state}`}>
          {activationStatus ? activationStatusLabel(activationStatus) : offerStateLabel(offer)}
        </span>
      </div>

      <div className="offer-card__body">
        <h2>{offer.title}</h2>
        <p>{offer.description}</p>
        {requirementText && (
          <div className="offer-card__requirement">
            <div className="offer-card__requirement-copy">
              <strong>{requirementText}</strong>
              {missingText && <span>{missingText}</span>}
            </div>
            {showProgress && (
              <ProgressBar
                value={offer.requirement.percent ?? 0}
                label="Прогресс до предложения"
              />
            )}
          </div>
        )}
      </div>

      <div className="offer-card__meta">
        <span>Активировали: {offer.activated_count.toLocaleString('ru-RU')}</span>
        {endDate && <span>До {endDate}</span>}
      </div>
    </>
  );
}

function CategoryNavigation({
  category,
  counts,
  onChange,
  mobile = false,
}: {
  category: OfferCategory;
  counts: Record<OfferCategory, number> | null;
  onChange: (category: OfferCategory) => void;
  mobile?: boolean;
}) {
  return (
    <nav className={mobile ? 'offer-categories offer-categories--mobile' : 'offer-categories'} aria-label="Доступность предложений">
      {CATEGORY_OPTIONS.map((option) => (
        <button
          className={category === option.value ? 'offer-category is-active' : 'offer-category'}
          key={option.value}
          type="button"
          aria-pressed={category === option.value}
          onClick={() => onChange(option.value)}
        >
          <span>{option.label}</span>
          <span className="offer-category__count">{counts?.[option.value] ?? '—'}</span>
        </button>
      ))}
    </nav>
  );
}

function ActiveOfferSummary({ activation, onOpen }: { activation: GuestActivation; onOpen: () => void }) {
  const expiry = formatOfferDate(activation.expires_at);

  return (
    <button className="offer-summary-card offer-summary-card--active" type="button" onClick={onOpen}>
      <span className="offer-summary-card__eyebrow">Активно</span>
      <strong>{activation.offer.title}</strong>
      {expiry && <small className="offer-summary-card__expiry">До {expiry}</small>}
    </button>
  );
}

function SoonOfferSummary({ offer, onOpen }: { offer: GuestOffer; onOpen: () => void }) {
  const missing = offerMissingText(offer) ?? offerRequirementText(offer);

  return (
    <button className="offer-summary-card offer-summary-card--soon" type="button" onClick={onOpen}>
      <span className="offer-summary-card__eyebrow">Скоро</span>
      <strong>{offer.title}</strong>
      <span>{offer.venue.display_name ?? 'Заведение Хоппера'}</span>
      {missing && <small>{missing}</small>}
      {offer.requirement.percent !== null && (
        <ProgressBar value={offer.requirement.percent} label="Прогресс до предложения" />
      )}
    </button>
  );
}

function OfferCard({
  offer,
  activating,
  onActivate,
  onOpenActivation,
  onOpenDetail,
}: {
  offer: GuestOffer;
  activating: boolean;
  onActivate: () => void;
  onOpenActivation: (activationId: string, offerId: string) => void;
  onOpenDetail: () => void;
}) {
  const activation = offer.my_activation;
  let primaryAction: React.ReactNode;

  if (activation?.status === 'active') {
    primaryAction = (
      <button className="primary-button offer-card__primary-action" type="button" onClick={() => onOpenActivation(activation.id, offer.id)}>
        Открыть активированное предложение
      </button>
    );
  } else if (activation?.status === 'redeemed') {
    primaryAction = <button className="offer-card__disabled-action" type="button" disabled>Получено</button>;
  } else if (activation?.status === 'expired' || activation?.status === 'revoked') {
    primaryAction = <button className="offer-card__disabled-action" type="button" disabled>Недоступно</button>;
  } else if (offer.state === 'available') {
    primaryAction = (
      <button className="primary-button offer-card__primary-action" type="button" disabled={activating} onClick={onActivate}>
        {activating ? 'Активируем…' : 'Активировать'}
      </button>
    );
  } else if (offer.state === 'soon') {
    primaryAction = <button className="offer-card__disabled-action" type="button" disabled>Скоро доступно</button>;
  } else {
    primaryAction = <button className="offer-card__disabled-action" type="button" disabled>Недоступно</button>;
  }

  return (
    <article className="offer-card offer-card--experience">
      <button className="offer-card__content-button" type="button" onClick={onOpenDetail}>
        <OfferCopy offer={offer} />
      </button>
      <div className="offer-card__actions">
        {primaryAction}
        <button className="offer-card__details-action" type="button" onClick={onOpenDetail}>Подробнее</button>
      </div>
    </article>
  );
}

export function OffersScreen({ mode, city, cityName, venueId, onOpenActivation, userContext, userContextActions }: OffersScreenProps) {
  const context = useMemo<OfferContext>(() => ({ mode, city, venueId }), [city, mode, venueId]);
  const [category, setCategory] = useState<OfferCategory>('all');
  const [offers, setOffers] = useState<GuestOffer[]>([]);
  const [counts, setCounts] = useState<Record<OfferCategory, number> | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [soonOffers, setSoonOffers] = useState<GuestOffer[]>([]);
  const [activations, setActivations] = useState<GuestActivation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);

  const loadOffers = useCallback(async (targetCategory: OfferCategory, targetPage: number, append: boolean) => {
    if (mode === 'venue' && !venueId) {
      setOffers([]);
      setCounts(null);
      setError('Не удалось определить заведение.');
      setLoading(false);
      return;
    }

    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const response = await guestOffersFetch<GuestOfferPage>(offersPageEndpoint(context, targetCategory, targetPage));
      setOffers((current) => append ? [...current, ...response.data] : response.data);
      setCounts(response.meta.counts);
      setPage(response.meta.current_page);
      setTotal(response.meta.total);
      setHasMore(response.meta.has_more);
    } catch {
      if (!append) setOffers([]);
      setError('Не удалось загрузить предложения. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [context, mode, venueId]);

  useEffect(() => {
    setPage(1);
    void loadOffers(category, 1, false);
  }, [category, loadOffers]);

  useEffect(() => {
    let cancelled = false;

    void guestOffersFetch<GuestOfferPage>(offersPageEndpoint(context, 'soon', 1))
      .then((response) => {
        if (!cancelled) setSoonOffers(response.data.slice(0, 2));
      })
      .catch(() => {
        if (!cancelled) setSoonOffers([]);
      });

    if (userContext.authenticated) {
      void guestOffersFetch<CollectionEnvelope<GuestActivation>>('/api/v1/me/offers')
        .then((response) => {
          if (!cancelled) setActivations(response.data);
        })
        .catch(() => {
          if (!cancelled) setActivations([]);
        });
    } else {
      setActivations([]);
    }

    return () => {
      cancelled = true;
    };
  }, [context, userContext.authenticated]);

  const activate = useCallback(async (offer: GuestOffer) => {
    if (offer.state !== 'available') {
      setFlash({ message: 'Предложение пока недоступно.', kind: 'error' });
      return;
    }

    setActivatingId(offer.id);
    setFlash(null);

    try {
      const commercialDeliveryToken = sponsoredOfferReference(offer.id);
      const response = await guestOffersFetch<DataEnvelope<GuestActivation>>(
        `/api/v1/offers/${encodeURIComponent(offer.id)}/activate`,
        {
          method: 'POST',
          body: JSON.stringify(commercialDeliveryToken ? { commercial_delivery_token: commercialDeliveryToken } : {}),
        },
      );
      clearSponsoredOfferReference(offer.id);
      onOpenActivation(response.data.id, offer.id);
    } catch (reason) {
      if (reason instanceof GuestOffersApiError) {
        if (reason.status === 401) {
          setFlash({ message: 'Войдите в Хоппер, чтобы активировать предложение', kind: 'error' });
          return;
        }

        if (reason.status === 409 && reason.code === 'offer_already_activated') {
          await loadOffers(category, 1, false);
          return;
        }

        if (reason.status === 409 && reason.code === 'offer_capacity_reached') {
          setFlash({ message: 'Мест больше нет', kind: 'error' });
          return;
        }

        if (reason.code === 'offer_soon' || reason.code === 'offer_locked') {
          setFlash({ message: 'Предложение пока недоступно.', kind: 'error' });
          return;
        }
      }

      setFlash({ message: 'Не удалось активировать предложение. Попробуйте ещё раз.', kind: 'error' });
    } finally {
      setActivatingId(null);
    }
  }, [category, loadOffers, onOpenActivation]);

  const openDetail = useCallback((offerId: string) => {
    window.location.assign(`/offers/${encodeURIComponent(offerId)}`);
  }, []);

  const activeActivations = activations.filter((activation) => activation.status === 'active');
  const venueName = offers[0]?.venue.display_name ?? soonOffers[0]?.venue.display_name ?? null;
  const cityLabel = cityName?.trim() || city;
  const hasRightRail = userContext.authenticated || soonOffers.length > 0;

  return (
    <main className="offers-page offers-page--experience" aria-label="Предложения Хоппера">
      <div className={hasRightRail ? 'offers-layout offers-layout--experience offers-layout--three-column' : 'offers-layout offers-layout--experience'}>
        <aside className="offers-rail panel-card offers-rail--experience">
          <span className="offers-eyebrow">Предложения</span>
          <h1>{mode === 'venue' ? venueName ?? 'Выбранное место' : cityLabel}</h1>
          <p>Выбирайте предложения по вашей реальной доступности.</p>

          <CategoryNavigation category={category} counts={counts} onChange={setCategory} />

          <div className="offers-rail__context">
            <Icon name={mode === 'venue' ? 'location' : 'map'} />
            <div>
              <span>{mode === 'venue' ? 'Заведение' : 'Город'}</span>
              <strong>{mode === 'venue' ? venueName ?? 'Выбранное место' : cityLabel}</strong>
            </div>
          </div>

          {userContext.authenticated && (
            <section className="offers-side-module offers-side-module--left-active" aria-label="Мои активные предложения">
              <div className="offer-section-heading">
                <strong>Мои активные</strong>
                <span>{activeActivations.length}</span>
              </div>
              {activeActivations.length > 0 ? (
                <div className="offer-summary-stack">
                  {activeActivations.slice(0, 3).map((activation) => (
                    <ActiveOfferSummary
                      key={activation.id}
                      activation={activation}
                      onOpen={() => onOpenActivation(activation.id, activation.offer.id)}
                    />
                  ))}
                </div>
              ) : (
                <p className="offers-side-module__empty">Нет активированных предложений.</p>
              )}
            </section>
          )}

          {category !== 'all' && (
            <button className="offers-reset-category" type="button" onClick={() => setCategory('all')}>Сбросить</button>
          )}
        </aside>

        <section className="offers-main offers-main--experience">
          <header className="offers-heading offers-heading--experience">
            <div>
              <span className="offers-eyebrow">{mode === 'venue' ? 'Предложения места' : 'Для вас в городе'}</span>
              <h1>Предложения</h1>
              <p>Доступность, прогресс и условия — только из данных Хоппера.</p>
            </div>
            {!loading && <span className="count-pill">{total}</span>}
          </header>

          <CategoryNavigation category={category} counts={counts} onChange={setCategory} mobile />

          {mode === 'city' && (
            <SponsoredPlacementCard city={city} surface="city_offers_featured" />
          )}

          {userContext.authenticated && activeActivations.length > 0 && (
            <section className="offers-mobile-active" aria-label="Мои активные предложения">
              <div className="offer-section-heading">
                <strong>Мои активные</strong>
                <span>{activeActivations.length}</span>
              </div>
              <div className="offer-summary-stack">
                {activeActivations.slice(0, 3).map((activation) => (
                  <ActiveOfferSummary
                    key={activation.id}
                    activation={activation}
                    onOpen={() => onOpenActivation(activation.id, activation.offer.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {flash && (
            <div className={`offer-flash is-${flash.kind}`} role="status">
              <span>{flash.message}</span>
              <button type="button" onClick={() => setFlash(null)} aria-label="Закрыть сообщение">×</button>
            </div>
          )}

          {loading ? (
            <div className="offers-state panel-card" role="status">
              <span className="map-state-card__pulse" />
              <strong>Загружаем предложения</strong>
              <span>Проверяем доступность и ваш прогресс.</span>
            </div>
          ) : error ? (
            <div className="offers-state panel-card">
              <strong>{error}</strong>
              <button className="primary-button" type="button" onClick={() => void loadOffers(category, 1, false)}>Попробовать снова</button>
            </div>
          ) : offers.length === 0 ? (
            <div className="offers-state panel-card">
              <Icon name="offer" />
              <strong>В этой категории пока нет предложений.</strong>
              <span>Попробуйте другой раздел.</span>
            </div>
          ) : (
            <>
              <div className="offers-list offers-list--experience">
                {offers.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    activating={activatingId === offer.id}
                    onActivate={() => void activate(offer)}
                    onOpenActivation={onOpenActivation}
                    onOpenDetail={() => openDetail(offer.id)}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="offers-load-more">
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={loadingMore}
                    onClick={() => void loadOffers(category, page + 1, true)}
                  >
                    {loadingMore ? 'Загружаем…' : 'Показать ещё'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {hasRightRail && (
          <aside className="offers-user-context-rail offers-user-context-rail--experience" aria-label="Контекст предложений">
            {userContext.authenticated && (
              <UserContextPanel data={userContext} actions={userContextActions} variant="compact" avatarShape="rounded-square" />
            )}

            {soonOffers.length > 0 && (
              <section className="offers-side-module" aria-label="Что откроется скоро">
                <div className="offer-section-heading">
                  <strong>Что откроется скоро</strong>
                </div>
                <div className="offer-summary-stack">
                  {soonOffers.map((offer) => (
                    <SoonOfferSummary key={offer.id} offer={offer} onOpen={() => openDetail(offer.id)} />
                  ))}
                </div>
              </section>
            )}
          </aside>
        )}
      </div>
    </main>
  );
}
